import * as i0 from '@angular/core';
import { Pipe, NgModule } from '@angular/core';
import { Subject, combineLatest, startWith, distinctUntilChanged } from 'rxjs';
import * as i1 from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';

const GPU_BREAKPOINTS = {
    xs: 0,
    sm: 576,
    md: 768,
    lg: 992,
    xl: 1200,
};
var GpuMediaQueryBreakpoints;
(function (GpuMediaQueryBreakpoints) {
    GpuMediaQueryBreakpoints["XS"] = "xs";
    GpuMediaQueryBreakpoints["SM"] = "sm";
    GpuMediaQueryBreakpoints["MD"] = "md";
    GpuMediaQueryBreakpoints["LG"] = "lg";
    GpuMediaQueryBreakpoints["XL"] = "xl";
    GpuMediaQueryBreakpoints["XS_DOWN"] = "xs-down";
    GpuMediaQueryBreakpoints["SM_DOWN"] = "sm-down";
    GpuMediaQueryBreakpoints["MD_DOWN"] = "md-down";
    GpuMediaQueryBreakpoints["LG_DOWN"] = "lg-down";
    GpuMediaQueryBreakpoints["SM_UP"] = "sm-up";
    GpuMediaQueryBreakpoints["MD_UP"] = "md-up";
    GpuMediaQueryBreakpoints["LG_UP"] = "lg-up";
    GpuMediaQueryBreakpoints["XL_UP"] = "xl-up";
})(GpuMediaQueryBreakpoints || (GpuMediaQueryBreakpoints = {}));
const GPU_MEDIA_QUERIES = {
    [GpuMediaQueryBreakpoints.XS]: `(max-width: ${GPU_BREAKPOINTS.sm - 0.02}px)`,
    [GpuMediaQueryBreakpoints.SM]: `(min-width: ${GPU_BREAKPOINTS.sm}px) and (max-width: ${GPU_BREAKPOINTS.md - 0.02}px)`,
    [GpuMediaQueryBreakpoints.MD]: `(min-width: ${GPU_BREAKPOINTS.md}px) and (max-width: ${GPU_BREAKPOINTS.lg - 0.02}px)`,
    [GpuMediaQueryBreakpoints.LG]: `(min-width: ${GPU_BREAKPOINTS.lg}px) and (max-width: ${GPU_BREAKPOINTS.xl - 0.02}px)`,
    [GpuMediaQueryBreakpoints.XL]: `(min-width: ${GPU_BREAKPOINTS.xl}px)`,
    [GpuMediaQueryBreakpoints.XS_DOWN]: `(max-width: ${GPU_BREAKPOINTS.sm - 0.02}px)`,
    [GpuMediaQueryBreakpoints.SM_DOWN]: `(max-width: ${GPU_BREAKPOINTS.md - 0.02}px)`,
    [GpuMediaQueryBreakpoints.MD_DOWN]: `(max-width: ${GPU_BREAKPOINTS.lg - 0.02}px)`,
    [GpuMediaQueryBreakpoints.LG_DOWN]: `(max-width: ${GPU_BREAKPOINTS.xl - 0.02}px)`,
    [GpuMediaQueryBreakpoints.SM_UP]: `(min-width: ${GPU_BREAKPOINTS.sm}px)`,
    [GpuMediaQueryBreakpoints.MD_UP]: `(min-width: ${GPU_BREAKPOINTS.md}px)`,
    [GpuMediaQueryBreakpoints.LG_UP]: `(min-width: ${GPU_BREAKPOINTS.lg}px)`,
    [GpuMediaQueryBreakpoints.XL_UP]: `(min-width: ${GPU_BREAKPOINTS.xl}px)`,
};

class GpuMediaQueryPipe {
    constructor(breakpointObserver, changeDetectorRef) {
        this.breakpointObserver = breakpointObserver;
        this.changeDetectorRef = changeDetectorRef;
        this.value = '';
        this.previousUrls = new Subject();
    }
    ngOnDestroy() {
        if (this.onBreakpointStateChange) {
            this.onBreakpointStateChange.unsubscribe();
            this.onBreakpointStateChange = null;
        }
    }
    transform(desktop, mobile, tablet) {
        this.urlsChanged(desktop, mobile, tablet);
        if (!this.onBreakpointStateChange) {
            this.onBreakpointStateChange = combineLatest([
                this.breakpointObserver.observe([
                    GPU_MEDIA_QUERIES[GpuMediaQueryBreakpoints.SM_DOWN],
                    GPU_MEDIA_QUERIES[GpuMediaQueryBreakpoints.LG_DOWN],
                ]),
                // This is needed for when any of the inputs change
                this.previousUrls.pipe(startWith([desktop, mobile, tablet]), distinctUntilChanged()),
            ]).subscribe(([breakpointState, [cDesktop, cMobile, cTablet]]) => {
                let value = cDesktop;
                if (cMobile && breakpointState.breakpoints[GPU_MEDIA_QUERIES[GpuMediaQueryBreakpoints.SM_DOWN]]) {
                    value = cMobile;
                }
                else if (cTablet && breakpointState.breakpoints[GPU_MEDIA_QUERIES[GpuMediaQueryBreakpoints.LG_DOWN]]) {
                    value = cTablet;
                }
                this.updateValue(value);
            });
        }
        return this.value;
    }
    urlsChanged(url1, url2, url3) {
        this.previousUrls.next([url1, url2, url3]);
    }
    updateValue(value) {
        if (this.value !== value) {
            this.value = value;
            this.changeDetectorRef.markForCheck();
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: GpuMediaQueryPipe, deps: [{ token: i1.BreakpointObserver }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "17.1.3", ngImport: i0, type: GpuMediaQueryPipe, name: "gpuMediaQuery", pure: false }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: GpuMediaQueryPipe, decorators: [{
            type: Pipe,
            args: [{ name: 'gpuMediaQuery', pure: false }]
        }], ctorParameters: () => [{ type: i1.BreakpointObserver }, { type: i0.ChangeDetectorRef }] });

class GpuLayoutModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: GpuLayoutModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.1.3", ngImport: i0, type: GpuLayoutModule, declarations: [GpuMediaQueryPipe], imports: [CommonModule], exports: [GpuMediaQueryPipe] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: GpuLayoutModule, providers: [GpuMediaQueryPipe], imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: GpuLayoutModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [GpuMediaQueryPipe],
                    imports: [CommonModule],
                    exports: [GpuMediaQueryPipe],
                    providers: [GpuMediaQueryPipe],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { GPU_BREAKPOINTS, GPU_MEDIA_QUERIES, GpuLayoutModule, GpuMediaQueryBreakpoints, GpuMediaQueryPipe };
